import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import * as React from 'react'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShieldSlashIcon from 'LuxKit/Icons/line/LineShieldSlashIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import Modal from 'LuxKit/Modal/Modal'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import type { PropertyCancellationLabelShape } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import { Refundable } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import Clickable from 'components/Common/Clickable'
import HelpPane from 'components/Common/HelpPane'
import ModalContext from 'contexts/ModalContext'
import CancellationPolicyTimeline from 'components/Common/CancellationPolicies/CancellationPolicyTimeline'
import cancellationPolicyTimelineMessages from 'components/Common/CancellationPolicies/cancellationPolicyTimelineMessages'

export interface PropertyCancellationPolicyViewProps {
  refundableStatus: Refundable
  refundDescription: string
  labelShape: PropertyCancellationLabelShape
  policyDescriptions: Array<string | undefined>
  /** Set when the policy reads as a timeline; the date the label shows. */
  timelineDeadline?: string
}

const RENDER_INFO_ICON = () => <LineInfoCircleIcon size="XS" />

function PropertyCancellationPolicyView({
  refundableStatus,
  refundDescription,
  labelShape,
  policyDescriptions,
  timelineDeadline,
}: PropertyCancellationPolicyViewProps) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const timelineModalTitle = intl.formatMessage(cancellationPolicyTimelineMessages.modalTitle)

  const openModal = useCallback(() => {
    showModal(<Modal title={timelineDeadline ? timelineModalTitle : refundDescription}>
      {timelineDeadline
        ? <CancellationPolicyTimeline deadline={timelineDeadline} policyDescriptions={policyDescriptions} />
        : policyDescriptions.map((line, i) => <BodyText variant="large" as="p" key={i}>{ line }</BodyText>)}
      <HelpPane />
    </Modal>)
  }, [showModal, refundDescription, timelineModalTitle, policyDescriptions, timelineDeadline])

  const shieldIcon = refundableStatus === Refundable.Non
    ? <LineShieldSlashIcon />
    : <SolidShieldCheckIcon colour="highlight-primary-dark" />

  const cancellationLabel = useMemo<React.ReactNode>(() => {
    if (labelShape.kind === 'non-refundable') return refundDescription
    const styledRefund = <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span" wrap="no-wrap" key="refund">{refundDescription}</BodyText>
    if (labelShape.kind === 'date' && labelShape.hotelOnly) {
      return intl.formatMessage(
        {
          id: 'nsXp9-TaxkcfoC_Mgmn8',
          defaultMessage: '{refundType} until {date} (hotel only)',
          description: 'Cancellation policy with date and hotel-only clarification',
        },
        { refundType: styledRefund, date: labelShape.formattedDate },
      )
    }
    if (labelShape.kind === 'date') {
      return intl.formatMessage(
        {
          id: 'nGO52oYKVABrD8dlQrKg',
          defaultMessage: '{refundType} until {date}',
          description: 'Cancellation policy with deadline date in the date selection or calendar picker',
        },
        { refundType: styledRefund, date: labelShape.formattedDate },
      )
    }
    if (labelShape.hotelOnly) {
      return intl.formatMessage({
        id: 'TU9nLufGKPNHd7PoClqw',
        defaultMessage: '{refundType} (hotel only)',
        description: 'Accommodation cancellation status without a headline date when another travel product is selected',
      }, { refundType: styledRefund })
    }
    return styledRefund
  }, [labelShape, intl, refundDescription])

  return <Clickable onClick={openModal}>
    <BodyTextBlock variant="medium" startIcon={shieldIcon}>
      <FormattedMessage
        id="aBR0Mwy9eVd8FXMnzQYq"
        defaultMessage="{cancellationLabel} <icon></icon>"
        description="Cancellation policy label with info icon in the cancellation flow"
        values={{
          cancellationLabel,
          icon: RENDER_INFO_ICON,
        }}
      />
    </BodyTextBlock>
  </Clickable>
}

export default PropertyCancellationPolicyView
